import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, useHistory } from 'react-router-dom';
import Button from '@cpg-its/ads.common.button';
import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import CheckIcon from '@material-ui/icons/Check';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { PageHeader } from '../../../global/components/layout/page-header';
import { ApiEndpoint } from '../../../global/constants/api-endpoints';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { downloadFileFromApiAndOpenNewTab } from '../../../global/helpers/file-helpers';
import { useBasePath } from '../../../global/hooks/useBasePath';
import { useParentRoute } from '../../../global/hooks/useParentRoute';
import { YesNoIndicators } from '../../../global/models/api-model';
import { DemographicAppKeys, DemographicsContext } from '../../../global/models/demographics-model';
import { TcsmStates } from '../../../root/tcsm-model';
import { AccountInfoRoutes } from '../../account-info/constants/routes';
import { AnnualEnrollmentProps } from '../../arc/models/arc-epi-props';
import { getRelationshipLabel, getTierLabel } from '../helpers/annual-enrollment-helpers';
import { AEDependent, AEPlanType, AnnualEnrollmentState, Enrollment, ProcessingStatus } from '../models/annual-enrollment-models';
import { selectedReviewItemStyles } from '../../../global/constants/theme/styles/components/selected-review-item';
import { ButtonWrapper } from '../../../global/components/layout/button-wrapper';
import { ArcRoutes } from '../../arc/constants/routes';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { TcsmModal } from '../../../global/components/tcsm-modal';
import { apiErrorClear } from '../../../global/ducks/base-api';
import { getAnnualEnrollmentInfo, postAnnualEnrollment } from '../../../global/ducks/mlps-api';
import { setAEInfo } from '../annual-enrollment-slice';
import { getCurrencyFormat, isNotApplicableRate } from '../../../global/helpers/format-helpers';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ExternalLink } from '../../../global/components/external-link-modal';
import { standAlone } from '../../../global/ducks/epi-api';
import { createNewForm } from '../../arc/constants/constant-strings';

const confirmationLink = '/document/pdf_form/ae_confirmation/client/';

export const NewFormAction = 'NewForm';
export const AbandonAction = 'Abandon';

interface Props {
	basePath?: string;
}

export function AnnualEnrollmentConfirmation(props: Props) {
	const app = useContext(DemographicsContext);
	const AESettings = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state.epiProps.annualEnrollmentSettingsState?.epiContent);
	const AEState = useSelector<TcsmStates, AnnualEnrollmentState>(state => state.annualEnrollment[app]);
	const [isDownloading, setIsDownloading] = useState(false);
	const editedMedicalPlan = (AEState?.AEInfo?.enrollments_edited?.M || [])[0];
	const editedDentalPlan = (AEState?.AEInfo?.enrollments_edited?.D || [])[0];
	const editedDependents = AEState?.AEInfo?.dep_enrollments_edited;
	const medicalDeclined = !editedMedicalPlan || !!editedMedicalPlan?.termination_date;
	const dentalDeclined = !editedDentalPlan || !!editedDentalPlan?.termination_date;
	const eligibleDependents = AEState?.AEInfo?.eligible_dependents;
	const token = useSelector<TcsmStates, string>(state => state.authenticatedState?.accessToken);
	const personGuid = useSelector<TcsmStates, string>(state => app === DemographicAppKeys.myCPG ? state?.personalInfo?.myCPG?.currentPerson?.person_guid : state?.personalInfo?.arc?.currentPerson?.person_guid);
	const guid = (app === DemographicAppKeys.myCPG) ? personGuid : useSelector<TcsmStates, string>(state => state.employmentState?.currentEmployee?.guid);
	const cpgHome = useBasePath(`${AccountInfoRoutes.MyCpgRoot}`);
	const mapHome = useBasePath(`${ArcRoutes.ArcRoot}`);
	const timeString = AEState?.AEInfo?.form_info?.change_id;
	const date = new Date(`${timeString.substring(0, 4)}-${timeString.substring(4, 6)}-${timeString.substring(6, 8)} ${timeString.substring(8, 10)}:${timeString.substring(10, 12)}:${timeString.substring(12, 14)}`);
	const formatedDate = date.toLocaleString('en-US', {
		weekday: 'short', // long, short, narrow
		day: 'numeric', // numeric, 2-digit
		year: 'numeric', // numeric, 2-digit
		month: 'short', // numeric, 2-digit, long, short, narrow
		hour: 'numeric', // numeric, 2-digit
		minute: 'numeric', // numeric, 2-digit
	});
	const parentRoute = useParentRoute();
	const buttonClasses = buttonStyles();
	const selectedItemClasses = selectedReviewItemStyles();

	const dispatch = useDispatch();
	const history = useHistory();
	const [open, setOpen] = React.useState(false);
	const isArc = app === DemographicAppKeys.arc;
	const isHeldForReview = AEState.AEInfo?.form_record?.processing_status === ProcessingStatus.InProgress &&
		AEState.AEInfo?.form_record?.processing_substatus === ProcessingStatus.HeldForReview;
	const isFailed = AEState.AEInfo?.form_record?.processing_status === ProcessingStatus.InProgress &&
		AEState.AEInfo?.form_record?.processing_substatus === ProcessingStatus.Failed;

	const completedForm = AEState.AEInfo?.form_record?.processing_status === ProcessingStatus.Completed
		|| AEState.AEInfo?.form_record?.processing_status === ProcessingStatus.Processed;

	const canBeAbandonedOrDeletedOrNewForm = AEState?.AEInfo?.form_info?.available_workflow_actions.Abandon || AEState?.AEInfo?.form_info?.available_workflow_actions.ClearChanges || AEState?.AEInfo?.form_info?.available_workflow_actions.NewForm;
	const noActions = AEState?.AEInfo?.form_info?.available_workflow_actions.None;

	const shouldShowNewFormButton = (!isHeldForReview && canBeAbandonedOrDeletedOrNewForm) && !noActions;

	const isHeldMessages = [].concat(AEState.AEInfo?.processing_messages?.mc_holds)?.concat(AEState.AEInfo?.processing_messages?.rq_holds)?.concat(AEState.AEInfo?.processing_messages?.tx_holds)?.filter(a => !!a);

	const myNotes = AEState.AEInfo?.global_edited?.memo || AEState.AEInfo?.global_base?.memo;
	const specialRequests = AEState.AEInfo?.global_edited?.special_requests || AEState.AEInfo?.global_base?.special_requests;

	let pageTitle = AESettings.confirmationPageTitle;
	let pageDescription = isArc ? AESettings.confirmationPageMapDescription : AESettings.confirmationPageDescription;

	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);
	const groupId = useSelector<TcsmStates, number>(state => state?.groupsState?.currentGroup?.group_id);

	if (isHeldForReview || isFailed) {
		pageTitle = isArc ? AESettings.adminHeldTitle : AESettings.heldTitle;
		pageDescription = isArc ? AESettings.adminHeldDescription : AESettings.heldDescription;
	}

	if (pageDescription) {
		pageDescription = pageDescription.replace('{date}', formatedDate);
	}

	const handleClose = () => {
		setOpen(false);
	};

	const handleOpen = () => {
		setOpen(true);
	};

	useEffect(() => {
		dispatch(apiErrorClear());
	});

	const abandonFormClick = () => {

		if (completedForm && noActions) {
			dispatch(getAnnualEnrollmentInfo(
				guid,
				0,
				(response) => {
					dispatch(setAEInfo(response, app));
					history.push(`${props.basePath}${AccountInfoRoutes.PersonalInfoSegment}`);
					sessionStorage?.setItem(`${createNewForm}-${guid}`, YesNoIndicators.Yes);
				},
				null,
				groupId,
			));
			return;
		}

		dispatch(postAnnualEnrollment(
			guid,
			{
				form_info: {
					change_id: AEState?.AEInfo?.form_info?.change_id,
					change_id_signature: AEState?.AEInfo?.form_info?.change_id_signature,
					workflow_action: AEState?.AEInfo?.form_info?.available_workflow_actions.Abandon ? AbandonAction : NewFormAction,
				},
			},
			(res) => {
				if (res?.processing_messages?.errors?.length > 0) {
					dispatch(setAEInfo(res, app));
					setOpen(false);
					window.scrollTo({
						top: 0,
						left: 0,
						behavior: 'smooth',
					});
				} else {
					dispatch(getAnnualEnrollmentInfo(guid, 0, (response) => {
						dispatch(setAEInfo(response, app));
						history.push(`${props.basePath}${AccountInfoRoutes.PersonalInfoSegment}`);
					}));
				}
			},
			null,
			groupId,
		));
	};

	// redirect if not submitted yet
	if (AEState.AEInfo?.form_record?.processing_status !== ProcessingStatus.InProgress && AEState.AEInfo?.form_record?.processing_status !== ProcessingStatus.Completed) {
		return <Redirect to={`${parentRoute}${AccountInfoRoutes.PersonalInfoSegment}`} />;
	}

	const coverageDependentsDisplay = (selectedPeople: AEDependent[], label: string) => {
		return (
			<div className={`divider-section-item ${selectedItemClasses.selectedReviewItem}`}>
				<h3 className='selected-review-item-header'>{label}</h3>
				<div className='selected-review-item-inner'>
					<div className='selected-review-item-content'>
						<CheckIcon className='selected-review-item-icon added' />
						<div>
							<p><strong>{AESettings.selfOptionText}</strong></p>
						</div>
					</div>
				</div>
				{selectedPeople.map(a => {
					return (<div className='selected-review-item-inner'>
						<div className='selected-review-item-content'>
							<CheckIcon className='selected-review-item-icon added' />
							<div>
								<p key={a?.person?._guid}><strong>{a?.person?.first} {a?.person?.middle} {a?.person?.last} {/*{AESettings.confirmationRelationLabelText}*/} </strong></p>
								<p className='input-caption'>{getRelationshipLabel(a?.relationship_code, AESettings)}</p>
							</div>
						</div>
					</div>
					);
				})}


			</div>);
	};

	const selectedCoveragePeopleDisplay = (coverageType: AEPlanType) => {
		const selectedPeopleGuid: string[] = [];
		const selectedPeople: AEDependent[] = [];
		if (coverageType === AEPlanType.Dental) {
			Object.keys(editedDependents).forEach(a => editedDependents[a]?.D === YesNoIndicators.Yes && selectedPeopleGuid.push(a));
			selectedPeopleGuid.forEach(a => selectedPeople.push(eligibleDependents.filter(b => b?.person?._guid === a)[0]));
			return coverageDependentsDisplay(selectedPeople, AESettings.dentalCoverageLabel);
		}

		Object.keys(editedDependents).forEach(a => editedDependents[a]?.M === YesNoIndicators.Yes && selectedPeopleGuid.push(a));
		selectedPeopleGuid.forEach(a => selectedPeople.push(eligibleDependents.filter(b => b?.person?._guid === a)[0]));
		return coverageDependentsDisplay(selectedPeople, AESettings.medicalCoverageLabel);

	};

	const planDisplay = (enrollment: Enrollment, coverageType: AEPlanType) => {
		const offer = coverageType === AEPlanType.Dental ?
			AEState?.AEInfo?.offerings?.D?.plans?.find(a => a.code === enrollment?.code) :
			AEState?.AEInfo?.offerings?.M?.plans?.find(a => a.code === enrollment?.code);
		return (
			<>
				<p><strong>{offer?.name}</strong></p>
				<p className='input-caption'>{getTierLabel(enrollment?.tier, AESettings)}</p>
				<p>{getCurrencyFormat(offer?.rates_by_tier[enrollment.tier], labelSetting, true)}</p>
			</>);
	};

	const selectedPlanDisplay = (coverageType: AEPlanType) => {
		return coverageType === AEPlanType.Dental ?
			planDisplay(editedDentalPlan, coverageType)
			: planDisplay(editedMedicalPlan, coverageType);
	};

	const medicalCoverageDisplay = () => {
		return medicalDeclined ?
			<div className='divider-section-item'>
				<h3>{AESettings.medicalCoverageLabel}</h3>
				<p><strong>{AESettings.declinedMedicalCoverageText}</strong></p>
			</div> :
			selectedCoveragePeopleDisplay(AEPlanType.Medical);
	};

	const dentalCoverageDisplay = () => {
		return dentalDeclined ?
			<div className='divider-section-item'>
				<h3>{AESettings.dentalCoverageLabel}</h3>
				<p>{AESettings.declinedDentalCoverageText}</p>
			</div> :
			selectedCoveragePeopleDisplay(AEPlanType.Dental);
	};

	const medicalPlanDisplay = () => {
		return (
			<div className='divider-section-item'>
				<h3>{AESettings.medicalPlanLabel}</h3>
				{medicalDeclined ?
					<p>{AESettings.declinedMedicalPlanText}</p> :
					selectedPlanDisplay(AEPlanType.Medical)}
			</div>);
	};

	const dentalPlanDisplay = () => {
		return (
			<div className='divider-section-item'>
				<h3>{AESettings.dentalPlanLabel}</h3>
				{dentalDeclined ?
					<p>{AESettings.declinedDentalPlanText}</p> :
					selectedPlanDisplay(AEPlanType.Dental)}
			</div>);
	};

	const downloadPdf = (downloadLink: string, fileName: string) => {
		setIsDownloading(true);
		downloadFileFromApiAndOpenNewTab(`${ApiEndpoint.mlps}${downloadLink}${personGuid}`, token, fileName, () => setIsDownloading(false));
	};

	function RenderHeldInReviewMessages() {

		if (!isHeldMessages || isHeldMessages?.length === 0) return null;

		return (
			<ContentSpacer>
				<Banner
					banner={{
						type: 'warning',
						label: <h5>{isArc ? AESettings.adminConfirmationHeldTitle : AESettings.confirmationHeldTitle}</h5>,
						description:
							<div className='rich-text'>
								<WysiwygWithReactLink content={isArc ? AESettings.adminConfirmationHeldDescription : AESettings.confirmationHeldDescription} />
								{
									isHeldMessages?.map((a, idx) => <p key={idx}>{a}</p>)
								}
							</div>,
					}}
				/>
			</ContentSpacer>
		);
	}

	function RenderButtonBar() {
		return (
			<ButtonWrapper flexEnd>
				{shouldShowNewFormButton && <Button buttonStyle='secondary' type='button' onClick={handleOpen} text={AESettings.abandonFormButtonText} />}
				{!standAlone && <Link className={buttonClasses.primaryButton} to={isArc ? mapHome : cpgHome}>{isArc ? AESettings.mapHomeLinkText : AESettings.myCpgHomeLinkText}</Link>}
			</ButtonWrapper>
		);
	}

	function RenderComments() {
		if (isArc && isHeldForReview && (myNotes || specialRequests)) {
			return (
				<DividerSection sectionText={AESettings?.commentsHeader}>
					<div className='rich-text'>
						{
							myNotes &&
							<>
								<h4>{AESettings?.myNotesLabel}</h4>
								<p>{myNotes}</p>
							</>
						}
						{
							specialRequests &&
							<>
								<h4>{AESettings?.specialRequestsLabel}</h4>
								<h6>{AESettings?.specialRequestsDescription}</h6>
								<p>{specialRequests}</p>
							</>
						}
					</div>
				</DividerSection>
			);
		}

		return null;
	}

	function DisplayNotApplicableRateMessage() {
		const medicalRate = AEState.AEInfo?.offerings?.M?.plans?.filter(x => x.code === editedMedicalPlan?.code)[0]?.rates_by_tier[editedMedicalPlan?.tier] || 0;
		const dentalRate = AEState.AEInfo?.offerings?.D?.plans?.filter(x => x.code === editedDentalPlan?.code)[0]?.rates_by_tier[editedDentalPlan?.tier] || 0;

		if ((!editedDentalPlan && !editedMedicalPlan)) return null;

		if ((editedMedicalPlan?.code && isNotApplicableRate(medicalRate)) || (editedDentalPlan?.code && isNotApplicableRate(dentalRate))) {
			return (<>
				<DividerSection removeDivider>
					<WysiwygWithReactLink content={AESettings?.annualEnrollmentNotApplicablePlanRateMessage} />
				</DividerSection>
			</>);
		}

		return null;
	}


	return (
		<>
			{
				AEState?.AEInfo?.processing_messages?.errors?.length > 0 &&
				<ContentSpacer>
					<Banner
						banner={{
							type: 'error',
							label: <p><strong>{AEState?.AEInfo?.processing_messages?.errors?.join(',')}</strong></p>,
						}}
					/>
				</ContentSpacer>
			}
			<RenderHeldInReviewMessages />
			{
				isArc &&
				<ContentSpacer>
					<WysiwygWithReactLink content={AESettings?.adminAccessMessage} />
				</ContentSpacer>
			}
			{
				noActions && 
				<ContentSpacer>
					<WysiwygWithReactLink content={AESettings?.myCpgNoneActionMessage} />
				</ContentSpacer>
			}
			<PageHeader
				headerText={pageTitle}
				description={pageDescription} />
			<DividerSection removeDivider>
				{
					(!medicalDeclined && editedMedicalPlan?.plan_info_url) &&
					<ExternalLink linkUrl={editedMedicalPlan?.plan_info_url} linkIcon={<PictureAsPdfIcon />} linkText={AESettings.medicalPlanPdfLinkText} />
				}
				{
					(!dentalDeclined && editedDentalPlan?.plan_info_url) &&
					<ExternalLink linkUrl={editedDentalPlan?.plan_info_url} linkIcon={<PictureAsPdfIcon />} linkText={AESettings.dentalPlanPdfLinkText} />
				}
				{
					isDownloading ? <LoadingCircle /> :
						<button className={`divider-section-item ${buttonClasses.linkButton}`} type='button' onClick={() => { downloadPdf(confirmationLink, 'transmission-confirmation.pdf'); }}><PictureAsPdfIcon /><span>{AESettings.transmissionPdfLinkText}</span></button>
				}
				<WysiwygWithReactLink content={AESettings?.healthPlanVendorMessage} />
			</DividerSection>
			<DividerSection removeDivider sectionText={AESettings.planDetailSectionTitle}>
				{AEState?.AEInfo?.offerings?.M?.plans?.length > 0 && medicalCoverageDisplay()}
				{AEState?.AEInfo?.offerings?.D?.plans?.length > 0 && dentalCoverageDisplay()}
				{AEState?.AEInfo?.offerings?.M?.plans?.length > 0 && medicalPlanDisplay()}
				{AEState?.AEInfo?.offerings?.D?.plans?.length > 0 && dentalPlanDisplay()}
			</DividerSection>
			<DisplayNotApplicableRateMessage />
			<RenderComments />
			<RenderButtonBar />
			<TcsmModal
				open={open}
				handleClose={handleClose}>
				<>
					<ContentSpacer>
						<div className='rich-text'>
							<div dangerouslySetInnerHTML={{ __html: AESettings.abandonFormModelDescription }} />
						</div>
					</ContentSpacer>
					<ButtonWrapper flexEnd>
						<Button type='button' buttonStyle='tertiary' text={AESettings.abandonFormModalCancelText} backgroundColor='#808080' onClick={handleClose} />
						<Button type='button' buttonStyle='primary' text={AESettings.abandonFormModalConfirmText} backgroundColor='#0000FF' onClick={abandonFormClick} />
					</ButtonWrapper>
				</>

			</TcsmModal>
		</>

	);
}

AnnualEnrollmentConfirmation.defaultProps =
{
	basePath: '',
};
